import { v4 as uuidv4 } from 'uuid';

const CACHE_KEY = 'PLANNER_PLACE_CACHE';
const CACHE_EXPIRY_DAYS = 30; // Cache data for 30 days
const MAX_CACHE_SIZE = 1000; // Maximum number of places to cache
const BATCH_SIZE = 50; // Number of places to fetch in one batch

const storage = {
  get: () => {
    try {
      const data = localStorage.getItem(CACHE_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Error reading cache:', error);
      return {};
    }
  },
  set: (data) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving cache:', error);
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }
};

export const PlaceCache = {
  cache: new Map(),
  
  async initialize() {
    try {
      const cache = storage.get();
      const now = Date.now();
      
      // Clean up expired entries
      const cleanedCache = {};
      let count = 0;
      
      Object.entries(cache).forEach(([key, value]) => {
        if (count >= MAX_CACHE_SIZE) return;
        if (value.timestamp && value.timestamp + (CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000) >= now) {
          cleanedCache[key] = value;
          this.cache.set(key, value);
          count++;
        }
      });
      
      storage.set(cleanedCache);
      return cleanedCache;
    } catch (error) {
      console.error('Error initializing cache:', error);
      return {};
    }
  },

  async getPlace(placeId) {
    try {
      const cache = storage.get();
      return cache[placeId];
    } catch (error) {
      console.error('Error getting place:', error);
      return null;
    }
  },

  async setPlace(placeId, data) {
    try {
      const cache = storage.get();
      if (Object.keys(cache).length >= MAX_CACHE_SIZE) {
        // Remove oldest entry
        const oldestKey = Object.entries(cache)
          .reduce((oldest, [key, value]) => 
            !oldest || value.timestamp < cache[oldest].timestamp ? key : oldest
          );
        delete cache[oldestKey];
      }
      
      cache[placeId] = {
        ...data,
        timestamp: Date.now()
      };
      storage.set(cache);
      this.cache.set(placeId, data);
    } catch (error) {
      console.error('Error setting place:', error);
    }
  },

  async batchGetPlaces(placeIds) {
    try {
      const cache = storage.get();
      const results = {};
      placeIds.forEach(id => {
        if (cache[id]) {
          results[id] = cache[id];
        }
      });
      return results;
    } catch (error) {
      console.error('Error batch getting places:', error);
      return {};
    }
  },

  async batchSetPlaces(places) {
    try {
      const cache = storage.get();
      Object.entries(places).forEach(([id, data]) => {
        if (Object.keys(cache).length >= MAX_CACHE_SIZE) {
          const oldestKey = Object.entries(cache)
            .reduce((oldest, [key, value]) => 
              !oldest || value.timestamp < cache[oldest].timestamp ? key : oldest
            );
          delete cache[oldestKey];
        }
        cache[id] = {
          ...data,
          timestamp: Date.now()
        };
      });
      storage.set(cache);
      places.forEach((data, id) => this.cache.set(id, data));
    } catch (error) {
      console.error('Error batch setting places:', error);
    }
  },

  async getAllPlaces() {
    const cache = storage.get();
    return Object.values(cache).filter(item => item.timestamp);
  },

  async clearCache() {
    storage.clear();
  }
};

// Initialize cache when the app starts
PlaceCache.initialize();
